/**
 * Sync Center — one screen for every vendor feed's sync status, every
 * EventBridge schedule, every catalog problem the server-side checks find, and
 * what changed on Shopify between check runs.
 *
 * Tabs (URL state, so links are shareable): ?tab=feeds | health | changes.
 * Catalog health click-through: ?tab=health&class=C1_BELOW_MAP&vendor=Nitto&stock=1&q=...
 */
import { useState, type ReactNode } from 'react';
import {
  Badge, Banner, BlockStack, Box, Button, Card, Checkbox, DataTable, Divider, InlineStack, Link, List, Modal,
  Page, Pagination, Select, Spinner, Tabs, Text, TextField, Tooltip,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  getChangeSummary, getChanges, getHealth, getIssueVendors, getIssues, getSyncFeeds, getSyncRuns, runHealthNow,
  type FeedRow, type FeedStatus, type HealthClass, type HealthResponse, type HealthRun, getVerification, type ConnectionCheck } from '../api/sync-center';
import { useDebouncedValue } from '../lib/useDebouncedValue';

const PAGE_SIZE = 50;
const TABS = [
  { id: 'feeds', content: 'Feeds & schedules' },
  { id: 'health', content: 'Catalog health' },
  { id: 'changes', content: 'Change log' },
];
const GROUP_LABEL: Record<string, string> = {
  identity: 'Identity', policy: 'Inventory policy', price: 'Price', stock: 'Stock', specs: 'Specs & labels',
  barcode: 'Barcode', listing: 'Listing', search: 'Search cards', coverage: 'Coverage',
};
const FIELD_LABEL: Record<string, string> = {
  price: 'Price', compare_at: 'Compare-at', stock: 'Stock', title: 'Title', image: 'Image', status: 'Status', policy: 'Policy',
};

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}
function fmtAge(hours: number | null | undefined): string {
  if (hours == null) return 'never';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}
function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  return fmtAge((Date.now() - new Date(iso).getTime()) / 3_600_000) + ' ago';
}
function n(v: number | null | undefined): string {
  return v == null ? '—' : v.toLocaleString();
}

function StatusBadge({ status }: { status: FeedStatus }) {
  if (status === 'green') return <Badge tone="success">Green</Badge>;
  if (status === 'amber') return <Badge tone="warning">Amber</Badge>;
  return <Badge tone="critical">Red</Badge>;
}
function LevelBadge({ level }: { level: string }) {
  if (level === 'fail') return <Badge tone="critical">Fail</Badge>;
  if (level === 'warn') return <Badge tone="warning">Warn</Badge>;
  return <Badge tone="info">Info</Badge>;
}
function StateBadge({ state }: { state: string }) {
  if (state === 'ENABLED') return <Badge tone="success">Enabled</Badge>;
  if (state === 'DISABLED') return <Badge tone="critical">Disabled</Badge>;
  return <Badge tone="attention">{state === 'NOT FOUND' ? 'Not found' : 'Unknown'}</Badge>;
}
function Delta({ value }: { value: number | null }) {
  if (value == null) return <Text as="span" tone="subdued">first run</Text>;
  if (value === 0) return <Text as="span" tone="subdued">no change</Text>;
  // More issues is bad (critical); fewer is good (success).
  return <Text as="span" tone={value > 0 ? 'critical' : 'success'} fontWeight="semibold">{value > 0 ? `▲ +${value.toLocaleString()}` : `▼ ${value.toLocaleString()}`}</Text>;
}

function useParamSetter() {
  const [params, setParams] = useSearchParams();
  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, v);
    }
    setParams(next);
  };
  return [params, set] as const;
}

export function SyncCenter() {
  const [params, setParam] = useParamSetter();
  const qc = useQueryClient();
  const tabId = params.get('tab') ?? 'feeds';
  const tabIndex = Math.max(0, TABS.findIndex((t) => t.id === tabId));
  const health = useQuery({ queryKey: ['sync-health'], queryFn: getHealth, refetchInterval: 30_000 });
  const active = health.data?.active ?? [];
  const run = useMutation({
    mutationFn: runHealthNow,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sync-health'] }); },
  });
  const [refreshing, setRefreshing] = useState(false);

  return (
    <Page
      fullWidth
      title="Sync Center"
      subtitle="Every vendor feed, every schedule and every catalog problem in one place. Checks run on the server every few hours; nothing here writes to Shopify."
      primaryAction={{
        content: active.length ? `Check run #${active[0].id} ${active[0].status}…` : 'Run checks now',
        onAction: () => run.mutate(),
        loading: run.isPending,
        disabled: run.isPending || active.length > 0 || health.data?.ready === false,
      }}
      secondaryActions={[{
        content: 'Refresh feed status',
        loading: refreshing,
        onAction: async () => {
          setRefreshing(true);
          try { await qc.fetchQuery({ queryKey: ['sync-feeds'], queryFn: () => getSyncFeeds(true) }); } finally { setRefreshing(false); }
        },
      }]}
    >
      <BlockStack gap="400">
        {run.error ? <Banner tone="critical" title="Could not start a check run">{(run.error as Error).message}</Banner> : null}
        {run.data ? <Banner tone="info" title={`Check run #${run.data.run_id} queued`} onDismiss={() => run.reset()}>{run.data.note}</Banner> : null}
        <Card padding="0">
          <Tabs tabs={TABS} selected={tabIndex} onSelect={(i) => setParam({ tab: TABS[i].id })} />
        </Card>
        {TABS[tabIndex].id === 'feeds' ? <FeedsTab /> : null}
        {TABS[tabIndex].id === 'health' ? <HealthTab health={health.data} loading={health.isLoading} error={health.error as Error | null} /> : null}
        {TABS[tabIndex].id === 'changes' ? <ChangesTab /> : null}
      </BlockStack>
    </Page>
  );
}

// ============================================================================ feeds

function FeedsTab() {
  const feeds = useQuery({ queryKey: ['sync-feeds'], queryFn: () => getSyncFeeds(), refetchInterval: 120_000 });
  const ver = useQuery({ queryKey: ['sync-verification'], queryFn: getVerification, refetchInterval: 120_000 });
  const vmap = new Map((ver.data?.connections ?? []).map((c) => [c.key, c] as const));
  const [selected, setSelected] = useState<FeedRow | null>(null);
  const [showAll, setShowAll] = useState(false);
  if (feeds.isLoading) return <Card><InlineStack align="center"><Spinner accessibilityLabel="Loading feeds" /></InlineStack></Card>;
  if (feeds.error) return <Banner tone="critical" title="Feed status failed to load">{(feeds.error as Error).message}</Banner>;
  const d = feeds.data!;
  const red = d.feeds.filter((f) => f.status === 'red');
  const order: Record<FeedStatus, number> = { red: 0, amber: 1, green: 2 };
  const rows = [...d.feeds].sort((a, b) => order[a.status] - order[b.status]).map((f) => [
    <StatusBadge key="s" status={f.status} />,
    <BlockStack key="f" gap="050">
      <Text as="span" fontWeight="semibold">{f.label}</Text>
      <Text as="span" tone="subdued" variant="bodySm">{f.vendor} · expected {f.cadence}</Text>
      {f.reasons.slice(0, 3).map((r, i) => (
        <Text key={i} as="span" variant="bodySm" tone={f.status === 'red' && i === 0 ? 'critical' : 'caution'}>{r}</Text>
      ))}
    </BlockStack>,
    <Tooltip key="t" content={f.last_success_at ?? 'never'}><Text as="span">{fmtTime(f.last_success_at)}</Text></Tooltip>,
    fmtAge(f.age_hours),
    n(f.rows_processed),
    <ShopifyCheck key="v" c={vmap.get(f.key)} />,
    <InlineStack key="j" gap="100" wrap>
      {f.schedules.map((s) => (
        <Tooltip key={s.name} content={`${s.name} · ${s.role} · ${s.schedule ?? 'no schedule'} · ${s.source}`}>
          <Badge tone={s.state === 'DISABLED' ? 'critical' : s.state === 'ENABLED' ? 'success' : 'attention'}>
            {`${s.role === 'primary' ? '' : '↳ '}${s.name.replace(/^(RoadRunner|RRW)-/, '')}`}
          </Badge>
        </Tooltip>
      ))}
      {f.lambdas.filter((l) => l.reserved_concurrency === 0).map((l) => (
        <Badge key={l.name} tone="critical">{`λ ${l.name} = 0`}</Badge>
      ))}
      {f.schedules.length === 0 && f.lambdas.length === 0 ? <Text as="span" tone="subdued" variant="bodySm">no schedule</Text> : null}
    </InlineStack>,
    <Button key="b" variant="plain" onClick={() => setSelected(f)}>Evidence</Button>,
  ]);
  const schedules = showAll ? d.schedules : d.schedules.filter((s) => s.flagged || s.feeds.length > 0);
  const schedRows = schedules.map((s) => [
    s.flagged ? <Tooltip key="f" content={s.flag_reason ?? ''}><Badge tone="critical">Off</Badge></Tooltip> : s.writes_live ? <Tooltip key="f" content="Writes live catalog data"><Badge tone="info">Live</Badge></Tooltip> : <Text key="f" as="span" tone="subdued">{s.category}</Text>,
    <BlockStack key="n" gap="050">
      <Text as="span" fontWeight="semibold">{s.name}</Text>
      {s.description ? <Text as="span" tone="subdued" variant="bodySm">{s.description.slice(0, 110)}{s.description.length > 110 ? '…' : ''}</Text> : null}
    </BlockStack>,
    <StateBadge key="st" state={s.state} />,
    s.schedule ?? '—',
    <Text key="sc" as="span" variant="bodySm">{s.lambda_fn ? `Lambda ${s.lambda_fn}` : s.script ?? s.target ?? '—'}</Text>,
    s.feeds.length ? s.feeds.join(', ') : '—',
    s.last_success_at ? ago(s.last_success_at) : '—',
    s.source === 'live' ? <Badge key="src" tone="success">live</Badge> : <Tooltip key="src" content={`EventBridge snapshot ${d.sources.schedule_snapshot_at}`}><Badge>snapshot</Badge></Tooltip>,
  ]);

  return (
    <BlockStack gap="400">
      <InlineStack gap="200" blockAlign="center">
        <Badge tone="critical">{`${d.counts.red} red`}</Badge>
        <Badge tone="warning">{`${d.counts.amber} amber`}</Badge>
        <Badge tone="success">{`${d.counts.green} green`}</Badge>
        <Badge tone={d.counts.flagged_schedules ? 'critical' : 'success'}>{`${d.counts.flagged_schedules} disabled schedules feed live data`}</Badge>
        <Text as="span" tone="subdued" variant="bodySm">Updated {ago(d.generated_at)}{d.cached ? ' (cached)' : ''}</Text>
      </InlineStack>
      {red.length ? (
        <Banner tone="critical" title={`${red.length} feed${red.length === 1 ? '' : 's'} need attention`}>
          <List>
            {red.map((f) => <List.Item key={f.key}><b>{f.label}</b>: {f.reasons[0] ?? 'red'}</List.Item>)}
          </List>
        </Banner>
      ) : null}
      <Card padding="0">
        <Box padding="400"><Text as="h2" variant="headingMd">Vendor feeds and sync jobs</Text>
          <Text as="p" tone="subdued" variant="bodySm">Red = a job that feeds it is off, it never ran, or its last success is older than 36 h. Amber = older than its normal cadence, a related job is off, or the latest attempt failed.</Text>
        </Box>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
          headings={['Status', 'Feed / why', 'Last success', 'Age', 'Rows', 'Shopify check', 'Jobs', '']}
          rows={rows}
          verticalAlign="top"
        />
      </Card>
      <Card padding="0">
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="050">
              <Text as="h2" variant="headingMd">Schedules (EventBridge)</Text>
              <Text as="p" tone="subdued" variant="bodySm">“Off” = DISABLED but the job writes live catalog data (feed tables or Shopify).</Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {d.sources.eventbridge_live_prefixes.length ? `Live state for ${d.sources.eventbridge_live_prefixes.join(', ')}* rules; ` : 'No rule could be read live; '}
                others from the read-only snapshot of {d.sources.schedule_snapshot_at.slice(0, 10)}.
              </Text>
            </BlockStack>
            <Checkbox label={`Show all ${d.schedules.length} schedules`} checked={showAll} onChange={setShowAll} />
          </InlineStack>
        </Box>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
          headings={['', 'Rule', 'State', 'Schedule', 'Runs', 'Feeds', 'Last success', 'Source']}
          rows={schedRows}
          verticalAlign="top"
        />
      </Card>
      <Banner tone="info" title="What the API role can and cannot read">
        <BlockStack gap="200">
          <Text as="p">
            Feed freshness comes from the database (import jobs, checkpoints, last sync timestamps) and the newest vendor file in S3.
            Lambda reserved concurrency is shown from the {d.sources.schedule_snapshot_at.slice(0, 10)} snapshot.
            {d.sources.eventbridge_errors.length ? ` EventBridge read errors: ${d.sources.eventbridge_errors.join('; ')}.` : ''}
          </Text>
          <Text as="p">To make every row live, the task role would need (not changed by this page):</Text>
          <List>{d.iam_needed.map((s) => <List.Item key={s}>{s}</List.Item>)}</List>
        </BlockStack>
      </Banner>
      {selected ? (
        <Modal open onClose={() => setSelected(null)} title={selected.label} size="large">
          <Modal.Section>
            <BlockStack gap="300">
              <InlineStack gap="200"><StatusBadge status={selected.status} /><Text as="span">Expected {selected.cadence} (max {selected.max_age_hours} h)</Text></InlineStack>
              {selected.notes ? <Text as="p" tone="subdued">{selected.notes}</Text> : null}
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'numeric', 'text']}
                headings={['Evidence', 'Kind', 'Time', 'Rows', 'Detail']}
                rows={selected.evidence.map((e) => [
                  e.source, e.role === 'applied' ? 'DB update' : 'file arrival', `${fmtTime(e.at)} (${ago(e.at)})`, n(e.rows),
                  [e.detail, e.error ? `error: ${e.error}` : null].filter(Boolean).join(' · ') || '—',
                ])}
              />
              {selected.lambdas.length ? (
                <Text as="p">Lambdas: {selected.lambdas.map((l) => `${l.name} (reserved concurrency ${l.reserved_concurrency ?? 'unrestricted'}, ${l.source})`).join('; ')}</Text>
              ) : null}
            </BlockStack>
          </Modal.Section>
        </Modal>
      ) : null}
    </BlockStack>
  );
}

// ============================================================================ catalog health

function RunLine({ run }: { run: HealthRun }) {
  const warn = (run.stats?.warnings as string[] | undefined) ?? [];
  return (
    <BlockStack gap="100">
      <InlineStack gap="200" blockAlign="center" wrap>
        <Text as="span" fontWeight="semibold">Run #{run.id}</Text>
        <Badge tone={run.status === 'succeeded' ? 'success' : run.status === 'failed' ? 'critical' : 'attention'}>{run.status}</Badge>
        <Text as="span" tone="subdued">{run.trigger}{run.requested_by ? ` by ${run.requested_by}` : ''}</Text>
        <Text as="span">Shopify read {fmtTime(run.shopify_exported_at)} ({ago(run.shopify_exported_at)})</Text>
        <Text as="span">{n(run.variants_checked)} variants checked</Text>
        <Text as="span">{n(run.total ?? run.issue_count)} issues{run.total_in_stock != null ? ` (${n(run.total_in_stock)} in stock)` : ''}</Text>
      </InlineStack>
      {warn.length ? <Text as="p" tone="caution" variant="bodySm">Warnings: {warn.join(' · ')}</Text> : null}
    </BlockStack>
  );
}

function HealthTab({ health, loading, error }: { health?: HealthResponse; loading: boolean; error: Error | null }) {
  const [params, setParam] = useParamSetter();
  if (loading) return <Card><InlineStack align="center"><Spinner accessibilityLabel="Loading catalog health" /></InlineStack></Card>;
  if (error) return <Banner tone="critical" title="Catalog health failed to load">{error.message}</Banner>;
  if (!health?.ready) return <Banner tone="warning" title="Catalog health is not set up yet">{health?.note ?? 'Run migration 083.'}</Banner>;
  const selectedClass = params.get('class') ?? '';
  const groups = Object.keys(GROUP_LABEL);
  const classes = [...health.classes].sort((a, b) => groups.indexOf(a.group) - groups.indexOf(b.group));
  const rows: ReactNode[][] = [];
  let lastGroup = '';
  for (const c of classes) {
    if (c.group !== lastGroup) {
      lastGroup = c.group;
      rows.push([<Text key="g" as="span" variant="headingSm">{GROUP_LABEL[c.group] ?? c.group}</Text>, '', '', '', '', '', '']);
    }
    rows.push(classRow(c, selectedClass === c.id, () => setParam({ class: c.id, vendor: undefined, page: undefined })));
  }
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Check runs</Text>
          {health.latest ? <RunLine run={health.latest} /> : <Text as="p" tone="subdued">No successful run yet. Use “Run checks now”, or wait for the worker’s schedule (every {health.schedule?.interval_hours ?? 6} h).</Text>}
          {health.active.map((r) => (
            <InlineStack key={r.id} gap="200" blockAlign="center">
              <Spinner size="small" accessibilityLabel="running" />
              <Text as="span">Run #{r.id} {r.status}{r.started_at ? ` since ${fmtTime(r.started_at)}` : ` (queued ${ago(r.created_at)})`}</Text>
            </InlineStack>
          ))}
          {health.last_failed ? (
            <Banner tone="critical" title={`Run #${health.last_failed.id} failed ${ago(health.last_failed.finished_at)}`}>
              <Text as="p">{health.last_failed.error ?? 'no error recorded'}</Text>
              <Text as="p" tone="subdued">The counts below are from the last successful run.</Text>
            </Banner>
          ) : null}
          {health.previous ? <Text as="p" tone="subdued" variant="bodySm">Changes are compared with run #{health.previous.id} ({fmtTime(health.previous.shopify_exported_at)}).</Text> : null}
        </BlockStack>
      </Card>
      <Card padding="0">
        <Box padding="400">
          <Text as="h2" variant="headingMd">Issue classes</Text>
          <Text as="p" tone="subdued" variant="bodySm">“In stock” = Shopify shows stock or the vendor has sellable stock. Retired listings (SUPERSEDED::) are excluded except from B3.</Text>
        </Box>
        <DataTable
          columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text', 'text', 'text']}
          headings={['Level', 'Issue', 'Count', 'In stock', 'vs previous run', 'Top vendors', '']}
          rows={rows}
          verticalAlign="top"
        />
      </Card>
      <IssueList runId={health.latest?.id} classes={health.classes} />
    </BlockStack>
  );
}

function classRow(c: HealthClass, selected: boolean, onView: () => void): ReactNode[] {
  return [
    <LevelBadge key="l" level={c.level} />,
    <Tooltip key="t" content={`${c.description} (ported from ${c.ported_from})`}>
      <BlockStack gap="050">
        <Text as="span" fontWeight={selected ? 'bold' : 'semibold'}>{c.title}</Text>
        <Text as="span" tone="subdued" variant="bodySm">{c.id}</Text>
      </BlockStack>
    </Tooltip>,
    <Text key="c" as="span" fontWeight="semibold" tone={c.count && c.level === 'fail' ? 'critical' : undefined}>{c.count.toLocaleString()}</Text>,
    c.in_stock.toLocaleString(),
    <BlockStack key="d" gap="050"><Delta value={c.delta} />{c.delta_in_stock ? <Text as="span" variant="bodySm" tone="subdued">in stock {c.delta_in_stock > 0 ? '+' : ''}{c.delta_in_stock}</Text> : null}</BlockStack>,
    <Text key="v" as="span" variant="bodySm">{c.top_vendors.slice(0, 3).map(([v, k]) => `${v} ${k}`).join(', ') || '—'}</Text>,
    c.count ? <Button key="b" size="slim" pressed={selected} onClick={onView}>View</Button> : '',
  ];
}

function IssueList({ runId, classes }: { runId?: number; classes: HealthClass[] }) {
  const [params, setParam] = useParamSetter();
  const cls = params.get('class') ?? '';
  const vendor = params.get('vendor') ?? '';
  const stock = params.get('stock') === '1';
  const page = Number(params.get('page') ?? '0') || 0;
  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = useDebouncedValue(search, 350);
  const issues = useQuery({
    queryKey: ['sync-issues', runId, cls, vendor, stock, q, page],
    queryFn: () => getIssues({ run_id: runId, class: cls || undefined, vendor: vendor || undefined, in_stock: stock ? true : undefined, q: q || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    enabled: runId != null,
  });
  const vendors = useQuery({ queryKey: ['sync-issue-vendors', runId, cls], queryFn: () => getIssueVendors({ run_id: runId, class: cls || undefined }), enabled: runId != null });
  if (runId == null) return null;
  const total = issues.data?.meta.total ?? 0;
  const classOptions = [{ label: 'All issue classes', value: '' }, ...classes.filter((c) => c.count > 0).map((c) => ({ label: `${c.title} (${c.count.toLocaleString()})`, value: c.id }))];
  const vendorOptions = [{ label: 'All vendors', value: '' }, ...(vendors.data?.data ?? []).map((v) => ({ label: `${v.vendor} (${v.count.toLocaleString()})`, value: v.vendor }))];
  const rows = (issues.data?.data ?? []).map((i) => [
    <BlockStack key="c" gap="050">
      <Text as="span" fontWeight="semibold">{i.class_title}</Text>
      {typeof i.details?.why === 'string' || Array.isArray(i.details?.why) ? <Text as="span" variant="bodySm" tone="subdued">{String(Array.isArray(i.details.why) ? (i.details.why as string[]).join(', ') : i.details.why)}</Text> : null}
      {typeof i.details?.note === 'string' ? <Text as="span" variant="bodySm" tone="subdued">{i.details.note as string}</Text> : null}
    </BlockStack>,
    <BlockStack key="s" gap="050">
      <Text as="span" fontWeight="semibold">{i.sku ?? '—'}</Text>
      <Text as="span" variant="bodySm" tone="subdued">{i.title ?? ''}</Text>
    </BlockStack>,
    <BlockStack key="v" gap="050"><Text as="span">{i.vendor ?? '—'}</Text><Text as="span" variant="bodySm" tone="subdued">{i.feed ?? ''}</Text></BlockStack>,
    i.shopify_value ?? '—',
    i.vendor_value ?? '—',
    i.in_stock ? <Badge key="st" tone="success">In stock</Badge> : <Text key="st" as="span" tone="subdued">—</Text>,
    <BlockStack key="l" gap="050">
      {i.admin_url ? <Link url={i.admin_url} target="_blank">Shopify admin</Link> : null}
      {i.storefront_url ? <Link url={i.storefront_url} target="_blank">Storefront</Link> : null}
      {!i.admin_url && !i.storefront_url ? <Text as="span" tone="subdued">not listed</Text> : null}
    </BlockStack>,
  ]);
  return (
    <Card padding="0">
      <Box padding="400">
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Issues {cls ? `— ${classes.find((c) => c.id === cls)?.title ?? cls}` : ''}</Text>
          <InlineStack gap="300" blockAlign="end" wrap>
            <Box minWidth="260px"><Select label="Issue class" options={classOptions} value={cls} onChange={(v) => setParam({ class: v, vendor: undefined, page: undefined })} /></Box>
            <Box minWidth="220px"><Select label="Vendor" options={vendorOptions} value={vendor} onChange={(v) => setParam({ vendor: v, page: undefined })} /></Box>
            <Box minWidth="220px">
              <TextField label="Search SKU / handle / title" value={search} autoComplete="off" clearButton
                onClearButtonClick={() => { setSearch(''); setParam({ q: undefined, page: undefined }); }}
                onChange={(v) => { setSearch(v); setParam({ q: v || undefined, page: undefined }); }} />
            </Box>
            <Checkbox label="In stock only" checked={stock} onChange={(v) => setParam({ stock: v ? '1' : undefined, page: undefined })} />
          </InlineStack>
          <Text as="p" tone="subdued" variant="bodySm">{issues.isLoading ? 'Loading…' : `${total.toLocaleString()} issues`}</Text>
        </BlockStack>
      </Box>
      {issues.error ? <Box padding="400"><Banner tone="critical">{(issues.error as Error).message}</Banner></Box> : null}
      <DataTable
        columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
        headings={['Issue', 'SKU', 'Vendor / feed', 'Shopify value', 'Vendor value', 'Stock', 'Links']}
        rows={rows}
        verticalAlign="top"
      />
      <Divider />
      <Box padding="300">
        <InlineStack align="center">
          <Pagination
            hasPrevious={page > 0}
            onPrevious={() => setParam({ page: String(page - 1) })}
            hasNext={(page + 1) * PAGE_SIZE < total}
            onNext={() => setParam({ page: String(page + 1) })}
            label={`Page ${page + 1} of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
          />
        </InlineStack>
      </Box>
    </Card>
  );
}

// ============================================================================ change log

function ChangesTab() {
  const [params, setParam] = useParamSetter();
  const runId = params.get('run') ? Number(params.get('run')) : undefined;
  const field = params.get('field') ?? '';
  const page = Number(params.get('cpage') ?? '0') || 0;
  const [search, setSearch] = useState(params.get('cq') ?? '');
  const q = useDebouncedValue(search, 350);
  const summary = useQuery({ queryKey: ['sync-change-summary'], queryFn: getChangeSummary, refetchInterval: 60_000 });
  const changes = useQuery({
    queryKey: ['sync-changes', runId, field, q, page],
    queryFn: () => getChanges({ run_id: runId, field: field || undefined, q: q || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
  });
  const syncRuns = useQuery({ queryKey: ['sync-runs'], queryFn: () => getSyncRuns(30), refetchInterval: 120_000 });
  const fields = ['price', 'stock', 'title', 'image', 'status', 'policy', 'compare_at'];
  const sumRows = (summary.data?.data ?? []).map((r) => [
    `#${r.run_id}`,
    fmtTime(r.observed_at),
    r.window_start ? `since ${fmtTime(r.window_start)}` : '—',
    ...fields.map((f) => (r.baseline ? '—' : n(r.fields?.[f] ?? 0))),
    r.baseline ? <Badge key="b" tone="info">baseline</Badge> : <Button key="b" size="slim" pressed={runId === r.run_id} onClick={() => setParam({ run: String(r.run_id), cpage: undefined })}>View</Button>,
  ]);
  const total = changes.data?.meta.total ?? 0;
  const rows = (changes.data?.data ?? []).map((c) => [
    fmtTime(c.observed_at),
    <BlockStack key="s" gap="050"><Text as="span" fontWeight="semibold">{c.sku ?? '—'}</Text><Text as="span" variant="bodySm" tone="subdued">{c.handle ?? ''}</Text></BlockStack>,
    c.vendor ?? '—',
    <Badge key="f">{FIELD_LABEL[c.field] ?? c.field}</Badge>,
    <Text key="ba" as="span">{`${c.before_value ?? '∅'} → ${c.after_value ?? '∅'}`}</Text>,
    <BlockStack key="l" gap="050">
      {c.admin_url ? <Link url={c.admin_url} target="_blank">Shopify admin</Link> : null}
      {c.storefront_url ? <Link url={c.storefront_url} target="_blank">Storefront</Link> : null}
    </BlockStack>,
  ]);
  const jobRows = (syncRuns.data?.data ?? []).map((j) => [
    fmtTime(j.created_at),
    j.feed,
    <Badge key="s" tone={j.status === 'applied' ? 'success' : j.status === 'failed' ? 'critical' : 'attention'}>{`${j.status}${j.dry_run ? ' (dry run)' : ''}`}</Badge>,
    n(j.rows), n(j.inserted), n(j.updated), n(j.invalid),
    fmtTime(j.applied_at),
    j.error ? <Text key="e" as="span" tone="critical" variant="bodySm">{j.error.slice(0, 120)}</Text> : '—',
  ]);
  return (
    <BlockStack gap="400">
      <Card padding="0">
        <Box padding="400">
          <Text as="h2" variant="headingMd">What changed on Shopify, per check run</Text>
          <Text as="p" tone="subdued" variant="bodySm">Each check run compares what Shopify shows now with the previous run, so changes made by any job (ECS cron, Lambda, script or a manual edit) appear here with before → after. The first run is the baseline.</Text>
        </Box>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'text']}
          headings={['Run', 'Observed', 'Window', 'Price', 'Stock', 'Title', 'Image', 'Status', 'Policy', 'Compare-at', '']}
          rows={sumRows}
        />
      </Card>
      <Card padding="0">
        <Box padding="400">
          <InlineStack gap="300" blockAlign="end" wrap>
            <Box minWidth="200px">
              <Select label="Run" value={runId ? String(runId) : ''} onChange={(v) => setParam({ run: v || undefined, cpage: undefined })}
                options={[{ label: 'All runs', value: '' }, ...(summary.data?.data ?? []).map((r) => ({ label: `#${r.run_id} · ${fmtTime(r.observed_at)}`, value: String(r.run_id) }))]} />
            </Box>
            <Box minWidth="180px">
              <Select label="Field" value={field} onChange={(v) => setParam({ field: v || undefined, cpage: undefined })}
                options={[{ label: 'All fields', value: '' }, ...fields.map((f) => ({ label: FIELD_LABEL[f], value: f }))]} />
            </Box>
            <Box minWidth="220px">
              <TextField label="Search SKU / handle" value={search} autoComplete="off" clearButton
                onClearButtonClick={() => { setSearch(''); setParam({ cq: undefined, cpage: undefined }); }}
                onChange={(v) => { setSearch(v); setParam({ cq: v || undefined, cpage: undefined }); }} />
            </Box>
            <Text as="span" tone="subdued">{changes.isLoading ? 'Loading…' : `${total.toLocaleString()} changes`}</Text>
          </InlineStack>
        </Box>
        {changes.error ? <Box padding="400"><Banner tone="critical">{(changes.error as Error).message}</Banner></Box> : null}
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
          headings={['Observed', 'SKU', 'Vendor', 'Field', 'Before → after', 'Links']}
          rows={rows}
          verticalAlign="top"
        />
        <Divider />
        <Box padding="300">
          <InlineStack align="center">
            <Pagination
              hasPrevious={page > 0}
              onPrevious={() => setParam({ cpage: String(page - 1) })}
              hasNext={(page + 1) * PAGE_SIZE < total}
              onNext={() => setParam({ cpage: String(page + 1) })}
              label={`Page ${page + 1} of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
            />
          </InlineStack>
        </Box>
      </Card>
      <Card padding="0">
        <Box padding="400">
          <Text as="h2" variant="headingMd">Vendor sync jobs (database)</Text>
          <Text as="p" tone="subdued" variant="bodySm">Latest runs recorded in import_jobs and tire_import_jobs: rows read, inserted and updated per run.</Text>
        </Box>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'text', 'text']}
          headings={['Started', 'Feed', 'Status', 'Rows', 'New', 'Updated', 'Invalid', 'Applied', 'Error']}
          rows={jobRows}
          verticalAlign="top"
        />
      </Card>
    </BlockStack>
  );
}

/** Per-connection verification against the live Shopify listings (latest check run). */
function ShopifyCheck({ c }: { c: ConnectionCheck | undefined }) {
  if (!c || c.verdict === 'n/a') return <Text as="span" tone="subdued" variant="bodySm">n/a</Text>;
  const tone = c.verdict === 'pass' ? 'success' : c.verdict === 'warn' ? 'attention' : 'critical';
  const label = c.verdict === 'pass' ? 'Verified' : `${c.issues.toLocaleString()} issue${c.issues === 1 ? '' : 's'}`;
  const tip = c.top_classes.map(([k, v]) => `${k}: ${v}`).join(' · ') || 'no issues';
  return (
    <Tooltip content={tip}>
      <BlockStack gap="050">
        <Badge tone={tone}>{label}</Badge>
        <Text as="span" tone="subdued" variant="bodySm">{`${c.in_stock_issues.toLocaleString()} in stock · ${c.checked.toLocaleString()} SKUs checked`}</Text>
      </BlockStack>
    </Tooltip>
  );
}
